import { useEffect, useState } from "react";
import { X, HelpCircle } from "lucide-react";

interface TabHelperBoxProps {
  title?: string;
  items: string[];
  note?: string;
  variant?: "info" | "tip";
  /** Klucz w localStorage do persystencji "ukryty" — np. "macro_helper". Bez prefixu hideHelp_. */
  storageKey?: string;
}

const VARIANTS = {
  info: {
    border: "border-blue-500/30",
    bg: "bg-accent",
    text: "text-accent-foreground",
  },
  tip: {
    border: "border-green-500/30",
    bg: "bg-accent",
    text: "text-accent-foreground",
  },
};

export function TabHelperBox({
  title = "Jak korzystac z tej zakladki?",
  items,
  note,
  variant = "info",
  storageKey,
}: TabHelperBoxProps) {
  const v = VARIANTS[variant];
  const fullKey = storageKey ? `hideHelp_${storageKey}` : null;
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!fullKey || typeof window === "undefined") return;
    setHidden(window.localStorage.getItem(fullKey) === "true");
  }, [fullKey]);

  const dismiss = () => {
    if (!fullKey || typeof window === "undefined") return;
    window.localStorage.setItem(fullKey, "true");
    setHidden(true);
  };

  const restore = () => {
    if (!fullKey || typeof window === "undefined") return;
    window.localStorage.removeItem(fullKey);
    setHidden(false);
  };

  // Tryb ukryty — pokaz tylko maly link "Pomoc" do przywrocenia
  if (hidden && fullKey) {
    return (
      <button
        type="button"
        onClick={restore}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/70 hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-muted/40"
        title="Pokaz instrukcje"
      >
        <HelpCircle className="h-3 w-3" />
        Pomoc
      </button>
    );
  }

  return (
    <div
      className={`relative rounded-md border ${v.border} ${v.bg} px-4 py-3 text-sm ${v.text} space-y-1`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium">{title}</p>
        {fullKey && (
          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 -mt-0.5 -mr-1 p-1 rounded hover:bg-foreground/10 transition-colors text-muted-foreground hover:text-foreground"
            title="Ukryj te wskazowki (mozesz przywrocic klikajac 'Pomoc')"
            aria-label="Zamknij"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <ul className="list-disc list-inside text-xs space-y-0.5">
        {items.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
        ))}
      </ul>
      {note && <p className="text-xs mt-1 opacity-80">{note}</p>}
    </div>
  );
}
